import React, { useCallback, useEffect, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { useInlineEdit } from '../../context/InlineEditContext';
import { Bold, Italic, List, ListOrdered, Link as LinkIcon, Loader2 } from 'lucide-react';

/**
 * EditableRichText — click-to-edit wrapper with a small floating TipTap toolbar.
 * Supports bold / italic / bullet list / ordered list / hyperlink.
 * Same save/dirty flow as EditableText: blur → autosave via saveField.
 *
 * Value is HTML (matches how CMS bios & rich fields are stored).
 */
export const EditableRichText = ({ scope, value, className = '', placeholder = 'Click to edit' }) => {
  const { enabled, saveField } = useInlineEdit();
  const [busy, setBusy] = useState(false);
  const [initialHtml, setInitialHtml] = useState(value || '');
  const [showToolbar, setShowToolbar] = useState(false);

  const editor = useEditor({
    editable: enabled,
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-[#00457f] underline' } }),
    ],
    content: value || '',
    onFocus: () => setShowToolbar(true),
    onBlur: async ({ editor }) => {
      // Delay so toolbar clicks (which blur then re-focus) still work
      setTimeout(async () => {
        if (!editor.isFocused) {
          setShowToolbar(false);
          const nextHtml = editor.getHTML();
          if (nextHtml !== initialHtml) {
            setBusy(true);
            const ok = await saveField({ ...scope, value: nextHtml });
            setBusy(false);
            if (ok) setInitialHtml(nextHtml);
            else editor.commands.setContent(initialHtml);
          }
        }
      }, 150);
    },
  }, [enabled]);

  useEffect(() => {
    if (editor && value !== undefined && editor.getHTML() !== value && !editor.isFocused) {
      editor.commands.setContent(value || '');
      setInitialHtml(value || '');
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(enabled);
  }, [enabled, editor]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL (leave blank to remove link)', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank' }).run();
  }, [editor]);

  if (!enabled) {
    return <div className={`${className} rich-text-view`} dangerouslySetInnerHTML={{ __html: value || '' }} />;
  }

  return (
    <div className={`inline-editable-rich ${className}`} style={{ position: 'relative' }}>
      {showToolbar && editor && (
        <div className="inline-rich-toolbar">
          <button type="button" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }} className={editor.isActive('bold') ? 'active' : ''} title="Bold"><Bold className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }} className={editor.isActive('italic') ? 'active' : ''} title="Italic"><Italic className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBulletList().run(); }} className={editor.isActive('bulletList') ? 'active' : ''} title="Bullet list"><List className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleOrderedList().run(); }} className={editor.isActive('orderedList') ? 'active' : ''} title="Numbered list"><ListOrdered className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); addLink(); }} className={editor.isActive('link') ? 'active' : ''} title="Link"><LinkIcon className="w-3.5 h-3.5" /></button>
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin ml-1 text-slate-400" />}
        </div>
      )}
      <EditorContent editor={editor} className="inline-tiptap" />
    </div>
  );
};
